import React from "react";
import toast from "react-hot-toast";

export const SUFFIX_SEP = ":::";
export const TRIP_SEP = "_trip_";

export const GuidanceBlock = ({ guidance }) => {
  if (!guidance || !Array.isArray(guidance) || guidance.length === 0) {
    return null;
  }
  return (
    <div className="mb-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
      <p className="text-xs font-semibold text-blue-800 mb-2">💡 Guidance:</p>
      <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
        {guidance.map((item, idx) => (
          <li key={idx} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const getFromAnswersHierarchy = (answers, triggerId, context = {}) => {
  let actual;
  const tripIdx = context.tripIndex ?? null;
  let suffix = context.optionSuffix ?? null;
  // 1. Try exact match first to avoid collision with suffixed keys of other questions
  if (answers?.[triggerId] !== undefined) {
    return answers[triggerId];
  }

  while (actual === undefined) {
    if (tripIdx !== null && suffix) {
      actual =
        answers?.[`${triggerId}${TRIP_SEP}${tripIdx}${SUFFIX_SEP}${suffix}`];
      if (actual === undefined) {
        actual = answers?.[`${triggerId}${TRIP_SEP}${tripIdx}_${suffix}`];
      }
      if (actual === undefined) {
        actual = answers?.[`${triggerId}_loop_${tripIdx}_${suffix}`];
      }
    }

    if (actual === undefined && suffix) {
      actual = answers?.[`${triggerId}${SUFFIX_SEP}${suffix}`];
      if (actual === undefined) {
        actual = answers?.[`${triggerId}_${suffix}`];
      }
    }

    if (actual === undefined && tripIdx !== null) {
      actual = answers?.[`${triggerId}${TRIP_SEP}${tripIdx}`];
      if (actual === undefined) {
        actual = answers?.[`${triggerId}_loop_${tripIdx}`];
      }
    }

    if (actual === undefined) {
      actual = answers?.[triggerId];
    }

    if (actual !== undefined || !suffix || !suffix.includes(SUFFIX_SEP)) {
      break;
    }

    suffix = suffix.substring(0, suffix.lastIndexOf(SUFFIX_SEP));
  }

  return actual;
};

export const evalCondition = (cond, answers = {}, context = {}) => {
  if (!cond) return true;

  // Non-capital goods table: show only when scope3_categories has selections
  // but capital_goods is NOT one of them
  if (
    cond.questionId === "scope3_categories_non_capital_goods_table" ||
    cond._id === "scope3_categories_non_capital_goods_table"
  ) {
    const getVal = (id) => {
      if (answers[id] !== undefined) return answers[id];
      const key = Object.keys(answers).find(
        (k) =>
          k === id ||
          k.startsWith(`${id}_`) ||
          k.startsWith(`${id}${SUFFIX_SEP}`),
      );
      return key ? answers[key] : undefined;
    };
    const scope3First = getVal("scope3_first");
    const scope3Categories = getVal("scope3_categories");
    const firstExists =
      scope3First !== undefined && scope3First !== null && scope3First !== "";
    if (!firstExists) return false;
    const categoriesList = Array.isArray(scope3Categories)
      ? scope3Categories
      : scope3Categories
        ? [scope3Categories]
        : [];
    return !categoriesList.includes("capital_goods");
  }

  const triggerId = cond.triggerQuestionId || cond.triggerFieldId;

  if (context.repeatingParentId && triggerId === context.repeatingParentId) {
    return true;
  }

  // question object
  if (cond.displayCondition) {
    const conditions = cond.displayCondition || [];
    if (conditions.length === 0) return true;

    // single condition -> ignore logicalOperator
    if (conditions.length === 1) {
      return evalCondition(conditions[0], answers, context);
    }

    const logicalOp = (cond.logicalOperator || "AND").toUpperCase();
    const check = (c) => evalCondition(c, answers, context);

    if (logicalOp === "OR") {
      return conditions.some(check);
    }

    return conditions.every(check);
  }

  // array of conditions
  if (Array.isArray(cond)) {
    if (cond.length === 0) return true;
    if (cond.length === 1) return evalCondition(cond[0], answers, context);

    const logicalOp = (context.logicalOperator || "AND").toUpperCase();

    if (logicalOp === "OR") {
      return cond.some((c) => evalCondition(c, answers, context));
    }

    return cond.every((c) => evalCondition(c, answers, context));
  }

  // single condition object
  if (!triggerId || !cond.operator) return true;

  const { operator, value: expected } = cond;
  let actual;
  let hasRowDataMatch = false;

  if (context.rowData) {
    const tripSuffixedId = `${triggerId}${TRIP_SEP}${context.tripIndex}`;
    const loopSuffixedId = `${triggerId}_loop_${context.tripIndex}`;

    const getValue = (data, id) => {
      if (Object.prototype.hasOwnProperty.call(data, id)) {
        return data[id];
      }
      // Fallback for nested array of objects (e.g., [{key: '...', value: 20}])
      // We look for any item value since evalCondition usually checks for existence or specific equality
      // This is a bit tricky without knowing the itemValue, but often triggerId is something like 'major_component_category'
      return undefined;
    };

    if (context.tripIndex !== null && context.tripIndex !== undefined) {
      if (
        Object.prototype.hasOwnProperty.call(context.rowData, tripSuffixedId)
      ) {
        actual = context.rowData[tripSuffixedId];
        hasRowDataMatch = true;
      } else if (
        Object.prototype.hasOwnProperty.call(context.rowData, loopSuffixedId)
      ) {
        actual = context.rowData[loopSuffixedId];
        hasRowDataMatch = true;
      }
    }

    if (
      !hasRowDataMatch &&
      Object.prototype.hasOwnProperty.call(context.rowData, triggerId)
    ) {
      actual = context.rowData[triggerId];
      hasRowDataMatch = true;
    }
  }

  if (!hasRowDataMatch) {
    actual = getFromAnswersHierarchy(answers, triggerId, context);
  }

  // Safe extraction for object-based values
  if (actual && typeof actual === "object" && !Array.isArray(actual)) {
    if (actual.value !== undefined) {
      actual = actual.value;
    } else if (actual.answer !== undefined) {
      actual = actual.answer;
    }
  }

  if (operator === "exists") {
    let exists = false;

    if (Array.isArray(actual)) {
      exists = actual.length > 0;
    } else if (
      typeof actual === "number" ||
      (!isNaN(actual) &&
        actual !== "" &&
        actual !== null &&
        actual !== undefined)
    ) {
      exists = parseFloat(actual) > 0;
    } else {
      exists = actual !== undefined && actual !== null && actual !== "";
    }

    return expected === false ? !exists : exists;
  }

  if (actual === undefined || actual === null || actual === "") {
    if (operator === "ne" || operator === "nq") return true;
    return false;
  }

  if (Array.isArray(actual)) {
    if (operator === "eq") {
      return actual.some((val) => String(val) === String(expected));
    }
    if (
      operator === "ne" ||
      operator === "nq" ||
      operator === "Not" ||
      operator === "not"
    ) {
      if (actual.length === 0) return true;
      return actual.every((val) => String(val) !== String(expected));
    }
  }

  const actualVal = actual ?? "";

  if (operator === "eq") return String(actualVal) === String(expected);
  if (
    operator === "ne" ||
    operator === "nq" ||
    operator === "Not" ||
    operator === "not"
  ) {
    return String(actualVal) !== String(expected);
  }

  return true;
};

export const getMultiselectDependency = (
  field,
  answers,
  questionsList = [],
  tripIndex = null,
  optionSuffix = null,
  extraFields = [],
) => {
  if (!field?.displayCondition) return null;

  const multiselectDeps = [];

  field.displayCondition.forEach((cond) => {
    const id = cond.triggerQuestionId || cond.triggerFieldId;
    const triggerField = (questionsList || []).find(
      (q) => (q.questionId || q.fieldId) === id || q._id === id,
    );

    const triggerType = triggerField?.questionType || triggerField?.fieldType;
    if (!["multiselect", "multi-select"].includes(triggerType)) return;

    if (cond.operator !== "exists" && cond.operator !== "eq") return;

    const selectedItems = getFromAnswersHierarchy(answers, id, {
      tripIndex,
      optionSuffix,
    });

    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      multiselectDeps.push({
        field: triggerField,
        selectedItems,
      });
    }
  });

  if (multiselectDeps.length === 0) return null;

  return {
    fields: multiselectDeps,
    triggerId:
      multiselectDeps[0]?.field?.questionId ||
      multiselectDeps[0]?.field?.fieldId,
    selectedItems: multiselectDeps[0]?.selectedItems || [],
  };
};

export const parseAnswerKey = (key) => {
  let questionId = key;
  let optionValue = null;
  let tripIndex = null;

  if (key.includes(SUFFIX_SEP)) {
    const parts = key.split(SUFFIX_SEP);
    questionId = parts[0];
    optionValue = parts[parts.length - 1];
  }

  if (questionId.includes(TRIP_SEP)) {
    const tripMatch = questionId.match(new RegExp(`(.*)${TRIP_SEP}(\\d+)`));
    if (tripMatch) {
      questionId = tripMatch[1];
      tripIndex = parseInt(tripMatch[2], 10);
    }
  } else if (questionId.includes("_loop_")) {
    const loopMatch = questionId.match(/(.*)_loop_(\d+)/);
    if (loopMatch) {
      questionId = loopMatch[1];
      tripIndex = parseInt(loopMatch[2], 10);
    }
  }

  return { questionId, tripIndex, optionValue };
};

export const validateInputValue = (question, rawValue) => {
  if (rawValue === "") return rawValue;

  const questionType = question?.questionType || question?.fieldType;
  const questionText = String(
    question?.questionText || question?.fieldLabel || "",
  ).toLowerCase();

  const isPercentage =
    questionType === "percentage" || questionText.includes("percentage");

  const numericValue = Number(rawValue);

  if (numericValue < 0) {
    toast.error("Negative values are not allowed");
    return "0";
  }

  if (isPercentage && numericValue > 100) {
    toast.error("Percentage cannot exceed 100%");
    return "100";
  }

  const limit = Array.isArray(question?.limit) ? question.limit[0] : null;

  if (limit && typeof limit === "string" && limit.includes("-")) {
    const [min, max] = limit.split("-").map(Number);

    if (!Number.isNaN(min) && numericValue < min) {
      toast.error(`Value cannot be less than ${min}`);
      return String(min);
    }

    if (!Number.isNaN(max) && numericValue > max) {
      toast.error(`Value cannot exceed ${max}`);
      return String(max);
    }
  }

  return rawValue;
};
